import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {Modal} from '@lightricks/react-design-system';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import {ROUTES} from '@/config/routesConstants';
import {ProfileProps} from '@/types/creatorProfile';
import {DashboardUser} from '@/types/dashboardUser';
import CreatorProfile from '@/components/creator-profile';
import {PROFILE_SOURCE_QUERY_PARAM} from '@/components/creator-profile/hooks/use-profile-source';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useCreatorProfileQuery from '@/hooks/queries/use-creator-profile-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import useToken from '@/hooks/use-token';
import CampaignNetworkContext from '@/contexts/CampaignNetworkContext';
import styles from './CreatorProfileModal.module.scss';
import getCampaignIdFromPathname from './getCampaignIdFromPathname';

function CreatorProfileModal() {
  const {isAuthenticated} = useToken(true, true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigation = useNavigation();
  const [navigationChecked, setNavigationChecked] = useState(false);
  const creatorProfileIdRedirect = searchParams.get('creatorProfileIdRedirect');
  const creatorProfileId = searchParams.get('creatorProfileId');
  const location = useLocation();

  const profileId = creatorProfileIdRedirect || creatorProfileId;

  const campaignId = getCampaignIdFromPathname(location.pathname);
  const {campaign} = useCampaignQuery({
    campaignId,
    enabled: !!campaignId && isAuthenticated,
  });
  const campaignNetwork = campaign?.network ?? undefined;

  const isOpen = !!profileId && isAuthenticated;

  useEffect(() => {
    // opened site with creatorProfileIdRedirect in query params =>
    //  navigate to the creator profile page instead of opening as a modal
    if (isAuthenticated && creatorProfileIdRedirect) {
      const profileUrl = campaignId
        ? `${ROUTES.CREATOR_PROFILE}/${creatorProfileIdRedirect}?campaignId=${campaignId}`
        : `${ROUTES.CREATOR_PROFILE}/${creatorProfileIdRedirect}`;
      navigation.navigate(profileUrl);
    }
    setNavigationChecked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, creatorProfileIdRedirect, campaignId]);

  useEffect(() => {
    if (!isAuthenticated && profileId) {
      handleClose();
    }
  }, [profileId, isAuthenticated]);

  // Legacy URL-sniffed abilities for the Ember-served `/campaigns/...`
  // routes. The new `/campaigns-v2/...` flow uses useProfileSource + the
  // `?source=` override (set by modal openers) and bypasses this block. To
  // be removed once the Ember routes are retired.
  const legacyModalAbilities = useMemo(() => {
    if (!profileId) return {};

    const lowerCasePathname = location.pathname.toLowerCase();
    if (!lowerCasePathname.startsWith('/campaigns/')) {
      return {};
    }

    const isSearch = lowerCasePathname.includes('search');
    const isProposal = lowerCasePathname.includes('proposals');
    const isInvite = lowerCasePathname.includes('candidates');

    return {
      componentAllowsCollaboration: isSearch,
      componentAllowsInviteToCampaign: isSearch,
      componentAllowsAddMembership: isSearch || isProposal || isInvite,
    };
  }, [location.pathname, profileId]);

  const {data: dashboardUser, isLoading: isLoadingDashboardUser} =
    useDashboardUserQuery({});

  const brandId = useBrandId();

  const queriesEnabled = !!profileId && !!brandId && isAuthenticated;

  const handleClose = () => {
    searchParams.delete('creatorProfileIdRedirect');
    searchParams.delete('creatorProfileId');
    searchParams.delete('activeTabId');
    searchParams.delete('activeSocialNetwork');
    // Clear the source override so the next opener doesn't inherit it.
    searchParams.delete(PROFILE_SOURCE_QUERY_PARAM);
    setSearchParams(searchParams, {replace: true});
  };

  const {
    profile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
  } = useCreatorProfileQuery({
    profileId,
    brandId,
    enabled: queriesEnabled,
  });

  useEffect(() => {
    if (isErrorProfile) {
      raiseFlashMessageError();
      handleClose();
    }
  }, [isErrorProfile]);

  const renderModalContent = () => {
    return (
      <div className={`${styles.modalContent}`}>
        <CreatorProfile
          testID={CREATOR_PROFILE_TEST_IDS.MAIN__MODAL}
          containerClassName={styles.creatorProfileModalContainer}
          isLoading={isLoadingProfile || isLoadingDashboardUser}
          profile={profile as ProfileProps}
          dashboardUser={dashboardUser as DashboardUser}
          displayPublicDataOnly={false}
          componentAllowsAddMembership
          componentAllowsCollaboration
          componentAllowsInviteToCampaign
          canViewAudience
          canViewSocialHealth
          canViewPortfolio
          canViewPosts
          canViewProfileNotes
          isModal
          {...legacyModalAbilities}
        />
      </div>
    );
  };

  if (!navigationChecked) {
    return null;
  }

  return (
    <CampaignNetworkContext.Provider value={campaignNetwork}>
      <Modal
        open={isOpen}
        showCloseButton={false}
        handleClose={handleClose}
        className={styles.modal}
      >
        {renderModalContent()}
      </Modal>
    </CampaignNetworkContext.Provider>
  );
}

export default CreatorProfileModal;
